// ============================================
// Event Bus — replaces Unity C# static events
// Central pub/sub for all module communication
// ============================================

class EventBus {
    constructor() {
        this._listeners = {};
    }

    on(event, callback) {
        if (!this._listeners[event]) this._listeners[event] = [];
        this._listeners[event].push(callback);
        return () => this.off(event, callback);
    }

    off(event, callback) {
        const arr = this._listeners[event];
        if (!arr) return;
        const idx = arr.indexOf(callback);
        if (idx >= 0) arr.splice(idx, 1);
    }

    emit(event, ...args) {
        const arr = this._listeners[event];
        if (!arr) return;
        // F03: iterate over a snapshot so once()-wrappers + handlers that
        // call off() / on() during emit don't shift the live array index
        // and skip / double-fire siblings. Listeners registered DURING emit
        // fire on the next emit, not this one — semantically clean.
        const snapshot = arr.slice();
        for (const cb of snapshot) {
            try { cb(...args); }
            catch (e) {
                // F25: include callback identity for grep-able logs.
                const tag = cb._scopeLabel || cb.name || '(anonymous)';
                console.error(`Event '${event}' handler [${tag}] error:`, e);
            }
        }
    }

    once(event, callback) {
        const wrapper = (...args) => {
            unsub();
            callback(...args);
        };
        const unsub = this.on(event, wrapper);
        return unsub;
    }

    /**
     * Create a scoped subscription collector.
     * All .on()/.once() calls through the scope are tracked.
     * Call scope.destroy() to unsubscribe all at once.
     * @param {string} label - debug label for logging
     * @returns {{ on, once, destroy, label }}
     */
    createScope(label) {
        const unsubs = [];
        const self = this;
        return {
            label,
            on(event, cb) {
                // F25: tag the callback so emit() can name it on error.
                try { cb._scopeLabel = label; } catch(_) {}
                const u = self.on(event, cb);
                unsubs.push(u);
                return u;
            },
            once(event, cb) {
                try { cb._scopeLabel = label; } catch(_) {}
                const u = self.once(event, cb);
                unsubs.push(u);
                return u;
            },
            destroy() {
                for (const u of unsubs) u();
                unsubs.length = 0;
            },
        };
    }
}

// Singleton event bus
export const events = new EventBus();

// ============================================
// Event names — mirrors Unity C# event system
// ============================================
export const Events = {
    // Story flow
    STORY_REFRESH_START: 'story:refreshStart', // Start of _processStoryContent — sinks snapshot baseline children for surgical-clear
    STORY_CLEAR: 'story:clear',             // Full clear (location change, game load)
    STORY_APPEND: 'story:append',           // New section — keep old content, add separator
    STORY_LOCATION_CHANGED: 'story:locationChanged', // Location changed but content kept (deferred-clear cancelled)
    STORY_INFO_TEXT: 'story:infoText',
    STORY_DIALOGUE: 'story:dialogue',
    STORY_IMAGE: 'story:image',
    STORY_VIDEO: 'story:video',
    STORY_CHOICES_READY: 'story:choicesReady',
    STORY_CONTENT_FINISHED: 'story:contentFinished',
    STORY_TYPING_START: 'story:typingStart',
    STORY_TYPING_STOP: 'story:typingStop',
    STORY_TYPING_COMPLETE: 'story:typingComplete', // All text cards done typing
    STORY_END: 'story:end',
    STORY_STOP_MEDIA: 'story:stopMedia',    // v0.2.9: synchronous, top-of-refreshView — detach hypno gain audio before any clear/rebuild (race-free)

    // Stats & variables
    STAT_CHANGED: 'stat:changed',           // (statName, newVal, oldVal)
    PACING_GAIN_SUPPRESSED: 'pacing:gainSuppressed', // (mode) — a tagged gain was fully zeroed by the pacing phase-lock
    NAME_CHANGED: 'name:changed',           // (which, newName)
    FLAG_CHANGED: 'flag:changed',           // (flagName, newVal)
    VARIABLE_CHANGED: 'variable:changed',   // (name, value)

    // Image preloading
    IMAGE_PRELOAD: 'image:preload',         // (imageNames[])

    // Tags
    TAG_UI: 'tag:ui',                       // (uiMode)
    TAG_IMAGE: 'tag:image',                 // (imageName)
    TAG_VIDEO: 'tag:video',                 // (videoName)
    TAG_CHAR: 'tag:char',                   // (speakerName)
    TAG_BGM: 'tag:bgm',                     // (trackName)
    TAG_SFX: 'tag:sfx',                     // (sfxName)
    TAG_AMBIENT: 'tag:ambient',             // (ambientName)
    TAG_ACHIEVEMENT: 'tag:achievement',     // (achievementId)
    TAG_STATS: 'tag:stats',                 // (statKey)
    TAG_SMS: 'tag:sms',                     // (sender, message, ...)
    TAG_SMS_REPLY: 'tag:smsReply',          // (options)
    TAG_RETURN: 'tag:return',               // (returnType)
    FORCED_EVENT_STARTED: 'story:forcedEventStarted',  // (knot) — telemetry breadcrumb; autosave gating reads storyManager.inForcedEvent directly
    TAG_ITEM: 'tag:item',                   // (action, itemTag)
    TAG_DISABLED: 'tag:disabled',           // (reason)
    TAG_BREAK: 'tag:break',                 // (delay)
    TAG_COLOR: 'tag:color',                 // (color)
    TAG_MONOLOGUE: 'tag:monologue',         // (text)
    TAG_SHOT: 'tag:shot',                   // (shotType: wide|close|hero|detail)
    TAG_CHAT: 'tag:chat',                   // (poolName) — #chat:pool emit, picks a reactive line

    // Inventory & equipment
    ITEM_ADDED: 'item:added',               // (item)
    ITEM_EQUIPPED: 'item:equipped',         // (item, slot)
    ITEM_UNEQUIPPED: 'item:unequipped',     // (item, slot)
    ITEM_PURCHASED: 'item:purchased',       // (item)
    BUFFS_RECALCULATED: 'buffs:recalculated',
    SET_BONUS_ACTIVATED: 'buffs:setActivated',  // (data: {name, inkFlag}) — fires once per set newly active

    // UI
    OVERLAY_OPEN: 'ui:overlayOpen',         // (overlayId)
    OVERLAY_CLOSE: 'ui:overlayClose',       // (overlayId) — REQUEST: layout listens and hides
    OVERLAY_CLOSED: 'ui:overlayClosed',     // (overlayId) — NOTIFICATION from layout.closeOverlay (fires on EVERY hide path: toggle, top, all, direct). Overlays with body-level satellites (gallery fullscreen viewer) tear them down on this.
    SCREEN_CHANGE: 'ui:screenChange',       // (screenId)
    TOAST_REQUEST: 'ui:toast',              // (message, cssClass)
    NOTIFICATION_REQUEST: 'ui:notification', // (text)
    TUTORIAL_TIP: 'ui:tutorialTip',         // (key, text)
    PHONE_NOTIFICATION: 'ui:phoneNotify',   // (sender, preview)

    // Audio
    AUDIO_SFX_PLAY: 'audio:sfxPlay',

    // Save/Load
    SAVE_STARTED: 'save:started',
    SAVE_COMPLETED: 'save:completed',
    SAVE_FAILED: 'save:failed',                  // (data: {slot, reason})
    SAVE_FALLBACK_FAILED: 'save:fallback_failed',// (data: {slot, reason, silent?})
    SAVE_LOSS_WARNING: 'save:loss_warning',      // (data: {lastPlayed})
    SAVE_HEALTH_ISSUE: 'save:health_issue',      // (data: {key, reason, lsNewerBy?}) F20 watchdog
    SAVE_PRIVATE_MODE: 'save:private_mode',      // (data: {quota, persistDenied}) probable incognito at init
    SAVE_EXPORT_DIAGNOSTIC: 'save:export_diagnostic', // (data: {day, hasInkState, inkStateLen, lsTs, idbTs, drift, isAuto, slot}) — fires on every Export Save click; analytics catches "always exports Day 1" class bugs without repro steps
    GAME_SAVED: 'cloud:gameSaved',               // (data: {slot, isAuto}) — fires after every successful manual save AND autosave; cloud-save.js push trigger
    CLOUD_SYNC_STATUS: 'cloud:status',           // (data: status() snapshot) — emitted by cloudSaveManager on sync/error transitions; settings UI live-updates
    CLOUD_SYNC_LIST_REFRESH: 'cloud:listRefresh',// (data: {slot?, reason?}) — remote slot set changed (push/adopt/delete); save-load UI refreshes badges
    LOAD_STARTED: 'load:started',
    LOAD_COMPLETED: 'load:completed',

    // Game flow
    GAME_NEW: 'game:new',
    GAME_LOADED: 'game:loaded',
    UNDO_PERFORMED: 'game:undoPerformed',          // (data: {remaining}) — story-screen Undo button consumed a snapshot
    UNDO_STACK_CHANGED: 'game:undoStackChanged',   // (data: {available}) — snapshot pushed/cleared, badge refresh

    // Achievement
    ACHIEVEMENT_UNLOCKED: 'achievement:unlocked', // (id, name, desc)

    // Localization
    LOCALE_CHANGED: 'locale:changed',             // (langCode)

    // Lovense (Patreon-gated, v0.2.4+)
    LOVENSE_PAIRED: 'lovense:paired',             // (data: {uid, toys, domain, platform})
    LOVENSE_DISCONNECTED: 'lovense:disconnected', // (data: {reason})
    LOVENSE_COMMAND_SENT: 'lovense:commandSent',  // (data: {command, action, timeSec, route: 'lan'|'cloud'})
    LOVENSE_TOY_LIST: 'lovense:toyList',          // (data: {toys})
};
